import React, {useState, useEffect} from 'react';
import {instrumentosAPI} from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';
import ErrorMessage from '../Common/ErrorMessage';
import Modal from '../Common/Modal';
import InstrumentForm from '../Forms/InstrumentForm';
import SearchFilter from '../Common/SearchFilter';
import DataTable from '../Common/DataTable';
import {PencilIcon, TrashIcon, PlusIcon} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {useAuth} from '../../context/AuthContext';
import {filterBySearchTerm} from '../../services/utils';

const Instruments = () => {
    const {isReadOnly} = useAuth();
    const [instruments, setInstruments] = useState([]);
    const [filteredInstruments, setFilteredInstruments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedInstrument, setSelectedInstrument] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchInstruments();
    }, []);

    const fetchInstruments = async () => {
        try {
            setLoading(true);
            const response = await instrumentosAPI.getAll();
            setInstruments(response.data);
            setFilteredInstruments(filterInstrumentsByTerm(response.data, searchTerm));
            setError(null);
        } catch (err) {
            setError('Error al cargar los instrumentos');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const filterInstrumentsByTerm = (instrumentsData, term) => {
        return filterBySearchTerm(
            instrumentsData,
            term,
            (instrument, lowerTerm) => instrument.Nombre.toLowerCase().includes(lowerTerm)
        );
    };

    const handleSearch = (searchTerm) => {
        setSearchTerm(searchTerm);
        setFilteredInstruments(filterInstrumentsByTerm(instruments, searchTerm));
    };

    const handleAdd = () => {
        setSelectedInstrument(null);
        setIsModalOpen(true);
    };

    const handleEdit = (instrument) => {
        setSelectedInstrument(instrument);
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Está seguro de eliminar este instrumento?')) {
            try {
                await instrumentosAPI.delete(id);
                toast.success('Instrumento eliminado correctamente');
                fetchInstruments();
            } catch (err) {
                toast.error('Error al eliminar el instrumento');
                console.error(err);
            }
        }
    };

    const handleSubmit = async (formData) => {
        try {
            if (selectedInstrument) {
                await instrumentosAPI.update(selectedInstrument.IDInstrumento, formData);
                toast.success('Instrumento actualizado correctamente');
            } else {
                await instrumentosAPI.create(formData);
                toast.success('Instrumento creado correctamente');
            }
            setIsModalOpen(false);
            fetchInstruments();
        } catch (err) {
            toast.error('Error al guardar el instrumento');
            console.error(err);
        }
    };

    const columns = [
        {
            key: 'Nombre',
            label: 'Nombre',
            sortable: true
        }
    ];

    // Agregar columna de acciones solo si no es readonly
    if (!isReadOnly()) {
        columns.push({
            key: 'actions',
            label: 'Acciones',
            render: (_, instrument) => (
                <div className="flex space-x-2">
                    <button
                        onClick={() => handleEdit(instrument)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Editar">
                        <PencilIcon className="h-5 w-5"/>
                    </button>
                    <button
                        onClick={() => handleDelete(instrument.IDInstrumento)}
                        className="text-red-600 hover:text-red-900"
                        title="Eliminar">
                        <TrashIcon className="h-5 w-5"/>
                    </button>
                </div>
            )
        });
    }

    if (loading) 
        return <LoadingSpinner text="Cargando instrumentos..."/>;
    if (error) 
        return <ErrorMessage message={error} onRetry={fetchInstruments}/>;
    
    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-900">Instrumentos</h1>
                {
                    !isReadOnly() && (
                        <button onClick={handleAdd} className="btn-primary">
                            <PlusIcon className="h-5 w-5 mr-2"/>
                            Nuevo Instrumento
                        </button>
                    )
                }
            </div>

            <SearchFilter
                onSearch={handleSearch}
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Buscar instrumento..."/>

            <div className="card">
                <DataTable
                    columns={columns}
                    data={filteredInstruments}
                    emptyMessage="No hay instrumentos registrados"/>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={selectedInstrument
                    ? 'Editar Instrumento'
                    : 'Nuevo Instrumento'}
                size="sm">
                <InstrumentForm
                    instrument={selectedInstrument}
                    onSubmit={handleSubmit}
                    onCancel={() => setIsModalOpen(false)}/>
            </Modal>
        </div>
    );
};

export default Instruments;
